import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { motion, useAnimation } from 'framer-motion';
import LogoMark from '../Utils/LogoMark';
import useNotificacionSonido from '../Utils/useNotificacionSonido';
import { apiFetch } from '../api/client';


const POLL_INTERVAL_MS = 2000;
const SHAKE_INTERVAL_MS = 4000;

const SIZE = 52;
const LOGO_SIZE = 40;
const ACTIVE_COLOR = '#F97316';
const IDLE_COLOR = 'rgba(255, 255, 255, 0.18)';

const Alerta = forwardRef(function Alerta(
  { onNavigate, soundEnabled = true, volume = 0.6, shakeIntervalMs = SHAKE_INTERVAL_MS },
  ref
) {
  const [count, setCount] = useState(0);
  const lastCountRef = useRef(0);
  const didInitRef = useRef(false);

  const isActive = count > 0;

  const playSound = useNotificacionSonido({ volume });
  const shakeControls = useAnimation();

  useImperativeHandle(ref, () => ({
    setCount: (n) => setCount(n),
  }));

  useEffect(() => {
    let stopped = false;
    let controller = null;
    let timer = null;

    const check = async () => {
      if (stopped || document.hidden) return;
      controller = new AbortController();
      try {
        const res = await apiFetch(`/sales?status=pending&limit=1`, {
          signal: controller.signal,
        });
        if (!res.ok) return;
        const data = await res.json();
        const total = Number(data.total) || 0;
        const previous = lastCountRef.current;
        if (previous !== total) {
          lastCountRef.current = total;
          setCount(total);

          if (soundEnabled && didInitRef.current && total > previous) {
            playSound();
          }
        }
        didInitRef.current = true;
      } catch {
        // error de red: mantener el último conteo
      }
    };

    const schedule = () => {
      if (stopped || document.hidden) return;
      timer = setTimeout(tick, POLL_INTERVAL_MS);
    };

    const tick = async () => {
      if (stopped) return;
      await check();
      schedule();
    };

    const handleVisibility = () => {
      if (timer) clearTimeout(timer);
      timer = null;
      if (document.hidden) return;
      check();
      schedule();
    };

    check();
    schedule();
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      if (controller) controller.abort();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Sacudida leve mientras existan pedidos pendientes */
  useEffect(() => {
    if (!isActive) {
      shakeControls.set({ x: 0 });
      return undefined;
    }

    let cancelled = false;
    let timer = null;

    const shake = () => {
      if (cancelled) return;
      shakeControls.start({
        x: [0, -5, 5, -3, 3, 0],
        transition: { duration: 0.45, ease: 'easeInOut' },
      });
      timer = setTimeout(shake, shakeIntervalMs);
    };

    timer = setTimeout(shake, shakeIntervalMs);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      shakeControls.stop();
    };
  }, [isActive, shakeControls, shakeIntervalMs]);

  return (
    <motion.div
      className="relative flex items-center justify-center"
      animate={shakeControls}
      style={{ width: SIZE, height: SIZE, cursor: 'pointer' }}
      onClick={() => {
        if (!isActive) return;
        onNavigate?.('ordenes');
      }}
      title={isActive ? `${count} ${count === 1 ? 'pedido pendiente' : 'pedidos pendientes'}` : 'Sin pedidos pendientes'}
    >
      {/* Logo — transparente en reposo, naranja con contador cuando hay pedidos */}
      <LogoMark
        size={LOGO_SIZE}
        logoColor={isActive ? ACTIVE_COLOR : IDLE_COLOR}
        cutColor="#000000"
        build={false}
      />

      {/* Contador de pedidos pendientes */}
      {isActive && (
        <span
          className="absolute flex items-center justify-center"
          style={{
            top: '-4px',
            right: '-4px',
            minWidth: '20px',
            height: '20px',
            borderRadius: '9999px',
            backgroundColor: ACTIVE_COLOR,
            color: '#fff',
            fontSize: '12px',
            fontWeight: 600,
            padding: '0 5px',
            fontFamily: '"Public Sans", sans-serif',
          }}
        >
          {count}
        </span>
      )}
    </motion.div>
  );
});

export default Alerta;